import React from 'react';
import { X, BarChart3, Users, Award, Clock } from 'lucide-react';

const AssessmentAnalytics = ({ assessment, submissions, onClose }) => {
  if (!assessment) return null;

  const totalSubmissions = submissions?.length || 0;
  const gradedSubmissions = submissions?.filter(s => s.status === 'graded') || [];
  const scores = gradedSubmissions.map(s => s.score || 0);
  const averageScore = scores.length > 0
    ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
    : 0;
  const highestScore = scores.length > 0 ? Math.max(...scores) : 0;
  const lowestScore = scores.length > 0 ? Math.min(...scores) : 0;
  const passingScore = assessment.passingScore || 50;
  const passCount = scores.filter(s => s >= passingScore).length;
  const passRate = scores.length > 0 ? Math.round((passCount / scores.length) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <BarChart3 className="w-5 h-5 text-sigma-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Assessment Analytics</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {assessment.title || 'Untitled Assessment'}
          </h3>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Users className="w-5 h-5 text-gray-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-gray-900">{totalSubmissions}</div>
              <div className="text-xs text-gray-500">Total Submissions</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Award className="w-5 h-5 text-gray-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-gray-900">{averageScore}%</div>
              <div className="text-xs text-gray-500">Average Score</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <BarChart3 className="w-5 h-5 text-gray-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-gray-900">{passRate}%</div>
              <div className="text-xs text-gray-500">Pass Rate</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Clock className="w-5 h-5 text-gray-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-gray-900">{gradedSubmissions.length}</div>
              <div className="text-xs text-gray-500">Graded</div>
            </div>
          </div>

          {/* Score Range */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Score Range</h4>
            <div className="flex justify-between text-sm">
              <div>
                <span className="text-gray-500">Highest:</span>{' '}
                <span className="font-medium text-green-600">{highestScore}%</span>
              </div>
              <div>
                <span className="text-gray-500">Average:</span>{' '}
                <span className="font-medium text-sigma-600">{averageScore}%</span>
              </div>
              <div>
                <span className="text-gray-500">Lowest:</span>{' '}
                <span className="font-medium text-red-600">{lowestScore}%</span>
              </div>
            </div>
            {scores.length > 0 && (
              <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-sigma-500 rounded-full"
                  style={{ width: `${averageScore}%` }}
                />
              </div>
            )}
          </div>

          {totalSubmissions === 0 && (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>No submissions yet to analyze.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-sigma-600 text-white rounded-lg hover:bg-sigma-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentAnalytics;
